import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, ArrowRight, CheckCircle, Loader2, 
  Building2, Target, Clock, DollarSign, MessageSquare, Users
} from "lucide-react";

const PROJECT_TYPES = [
  "Business Setup",
  "Graphic Design", 
  "SOP Creation",
  "Admin Support",
  "Social Media",
  "SEO",
  "Marketing",
  "Media Promo",
  "Other"
];

const TIMELINE_OPTIONS = [
  "ASAP (Within 1 week)",
  "Short-term (2-4 weeks)",
  "Medium-term (1-2 months)",
  "Long-term (3+ months)",
  "Ongoing/Retainer",
  "Flexible"
];

const BUDGET_OPTIONS = [
  "Under $500",
  "$500 - $1,000",
  "$1,000 - $2,500",
  "$2,500 - $5,000",
  "$5,000 - $10,000",
  "$10,000+",
  "To be discussed"
];

const COMMUNICATION_OPTIONS = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone/Video Call" },
  { id: "slack", label: "Slack/Teams" },
  { id: "text", label: "Text/SMS" }
];

export const ProjectIntakePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [existingIntake, setExistingIntake] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Business Info
    business_name: "",
    industry: "",
    business_description: "",
    website: "",
    
    // Step 2: Project Details
    project_type: "",
    project_description: "",
    goals: "",
    key_deliverables: "",
    
    // Step 3: Timeline & Budget
    timeline: "",
    budget_range: "",
    start_date: "",
    
    // Step 4: Communication & Preferences
    preferred_communication: [],
    communication_frequency: "weekly",
    availability: "",
    
    // Step 5: Context & References
    current_challenges: "",
    desired_outcomes: "",
    competitor_references: "",
    additional_notes: ""
  });

  useEffect(() => {
    fetchExistingIntake();
  }, []);

  const fetchExistingIntake = async () => {
    try {
      const response = await authFetch(`${API}/project-intake`, {  });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setExistingIntake(data);
          setFormData(prev => ({ ...prev, ...data }));
        }
      }
    } catch (_error) {
      console.error("Error fetching intake:", _error);
    }
  };

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleCommunication = (id) => {
    setFormData(prev => ({
      ...prev,
      preferred_communication: prev.preferred_communication.includes(id)
        ? prev.preferred_communication.filter(c => c !== id)
        : [...prev.preferred_communication, id]
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/project-intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Project intake submitted successfully!");
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.business_name && formData.industry;
      case 2:
        return formData.project_type && formData.project_description;
      case 3:
        return formData.timeline && formData.budget_range;
      case 4:
        return formData.preferred_communication.length > 0;
      case 5:
        return formData.desired_outcomes;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="project-intake">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-neutral-900">Project Requirements Intake</h1>
              <p className="text-sm text-neutral-500">Tell us about your project needs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-neutral-600 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Step 1: Business Info */}
        {step === 1 && (
          <Card className="border-neutral-200">
            <CardHeader className="bg-emerald-50 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Business Information</CardTitle>
                  <CardDescription>Tell us about your business</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => updateField("business_name", e.target.value)}
                    placeholder="Your Company LLC"
                    data-testid="business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => updateField("industry", e.target.value)}
                    placeholder="e.g., Healthcare, E-commerce, Coaching"
                    data-testid="industry"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Business Description</Label>
                <Textarea
                  value={formData.business_description}
                  onChange={(e) => updateField("business_description", e.target.value)}
                  placeholder="Briefly describe what your business does and who you serve..."
                  className="min-h-24"
                />
              </div>
              <div className="space-y-2">
                <Label>Website (optional)</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => updateField("website", e.target.value)}
                  placeholder="https://yourbusiness.com"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Project Details */}
        {step === 2 && (
          <Card className="border-neutral-200">
            <CardHeader className="bg-blue-50 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Project Details</CardTitle>
                  <CardDescription>What do you need help with?</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label>Project Type *</Label>
                <Select value={formData.project_type} onValueChange={(v) => updateField("project_type", v)}>
                  <SelectTrigger data-testid="project-type">
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Project Description *</Label>
                <Textarea
                  value={formData.project_description}
                  onChange={(e) => updateField("project_description", e.target.value)}
                  placeholder="Describe what you need in detail..."
                  className="min-h-28"
                  data-testid="project-description"
                />
              </div>
              <div className="space-y-2">
                <Label>Goals & Objectives</Label>
                <Textarea
                  value={formData.goals}
                  onChange={(e) => updateField("goals", e.target.value)}
                  placeholder="What do you want to achieve with this project?"
                  className="min-h-24"
                />
              </div>
              <div className="space-y-2">
                <Label>Key Deliverables</Label>
                <Textarea
                  value={formData.key_deliverables}
                  onChange={(e) => updateField("key_deliverables", e.target.value)}
                  placeholder="List the specific outputs you expect (e.g., logo files, SOPs, social posts)..."
                  className="min-h-24"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Timeline & Budget */}
        {step === 3 && (
          <Card className="border-neutral-200">
            <CardHeader className="bg-purple-50 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Timeline & Budget</CardTitle>
                  <CardDescription>Help us understand your constraints</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label>Timeline Expectation *</Label>
                <Select value={formData.timeline} onValueChange={(v) => updateField("timeline", v)}>
                  <SelectTrigger data-testid="timeline">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMELINE_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget Range *</Label>
                <Select value={formData.budget_range} onValueChange={(v) => updateField("budget_range", v)}>
                  <SelectTrigger data-testid="budget">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preferred Start Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => updateField("start_date", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Communication */}
        {step === 4 && (
          <Card className="border-neutral-200">
            <CardHeader className="bg-orange-50 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Communication Preferences</CardTitle>
                  <CardDescription>How would you like to stay in touch?</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3">
                <Label>Preferred Communication Methods *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {COMMUNICATION_OPTIONS.map(opt => (
                    <div 
                      key={opt.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.preferred_communication.includes(opt.id)
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-neutral-200 hover:border-neutral-300"
                      }`}
                      onClick={() => toggleCommunication(opt.id)}
                    >
                      <Checkbox 
                        checked={formData.preferred_communication.includes(opt.id)}
                        onCheckedChange={() => toggleCommunication(opt.id)}
                      />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label>How often would you like updates?</Label>
                <RadioGroup 
                  value={formData.communication_frequency} 
                  onValueChange={(v) => updateField("communication_frequency", v)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="daily" id="daily" />
                    <Label htmlFor="daily" className="font-normal">Daily</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="weekly" id="weekly" />
                    <Label htmlFor="weekly" className="font-normal">Weekly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="biweekly" id="biweekly" />
                    <Label htmlFor="biweekly" className="font-normal">Bi-weekly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="asneeded" id="asneeded" />
                    <Label htmlFor="asneeded" className="font-normal">As needed</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label>Your Availability</Label>
                <Textarea
                  value={formData.availability}
                  onChange={(e) => updateField("availability", e.target.value)}
                  placeholder="Best days/times to reach you, timezone, etc."
                  className="min-h-20"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Context & References */}
        {step === 5 && (
          <Card className="border-neutral-200">
            <CardHeader className="bg-teal-50 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle>Context & References</CardTitle>
                  <CardDescription>Help us understand your situation better</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label>Current Challenges</Label>
                <Textarea
                  value={formData.current_challenges}
                  onChange={(e) => updateField("current_challenges", e.target.value)}
                  placeholder="What obstacles or pain points are you facing right now?"
                  className="min-h-24"
                />
              </div>
              <div className="space-y-2">
                <Label>Desired Outcomes *</Label>
                <Textarea
                  value={formData.desired_outcomes}
                  onChange={(e) => updateField("desired_outcomes", e.target.value)}
                  placeholder="What does success look like for this project? How will you measure it?"
                  className="min-h-24"
                  data-testid="desired-outcomes"
                />
              </div>
              <div className="space-y-2">
                <Label>Competitor/Inspiration References</Label>
                <Textarea
                  value={formData.competitor_references}
                  onChange={(e) => updateField("competitor_references", e.target.value)}
                  placeholder="Any competitors or examples you admire? Include links if available."
                  className="min-h-20"
                />
              </div>
              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => updateField("additional_notes", e.target.value)}
                  placeholder="Anything else we should know?"
                  className="min-h-20"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(1, s - 1))}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-intake"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Intake
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectIntakePage;
